import React from 'react';
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  pdf,
} from '@react-pdf/renderer';
import { OrderViewData } from 'src/models/Management.interface';
import { getCompanyInfoForPDF, CompanyInfoData } from 'src/utils/company.utils';
import { formatDate, formatCurrency } from 'src/utils/pdf.utils';
import Logo from 'src/assets/images/Logo.png';

const colors = {
  primary: '#1a3c5e',
  primaryLight: '#3a7bd5',
  textDark: '#1a1a2e',
  textMedium: '#555555',
  textLight: '#888888',
  border: '#e0e0e0',
  borderLight: '#f0f0f0',
  backgroundGray: '#f7f8fa',
  white: '#ffffff',
  orange: '#e67e22',
  blue: '#3a7bd5',
  success: '#27ae60',
  red: '#c0392b',
};

const styles = StyleSheet.create({
  page: {
    paddingTop: 40,
    paddingLeft: 40,
    paddingRight: 40,
    paddingBottom: 100,
    fontSize: 10,
    fontFamily: 'Helvetica',
    backgroundColor: colors.white,
    fontWeight: 'bold',
  },

  headerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  headerLeft: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  companyLogo: {
    width: 36,
    height: 36,
    marginBottom: 8,
  },
  companyTextBlock: {
    flexDirection: 'column',
  },
  companyName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: colors.primary,
    marginBottom: 2,
  },
  companyCountry: {
    fontSize: 8,
    color: colors.textMedium,
    fontWeight: 'normal',
    marginBottom: 1,
  },
  companyDetails: {
    fontSize: 9,
    color: colors.textMedium,
    fontWeight: 'normal',
    marginBottom: 2,
  },
  headerRight: {
    width: '40%',
    alignItems: 'flex-end',
  },
  grandTotalHeaderLabel: {
    fontSize: 9,
    color: colors.textLight,
    marginBottom: 4,
    textAlign: 'right',
  },
  grandTotalHeaderValue: {
    fontSize: 16,
    fontWeight: 'bold',
    color: colors.textDark,
    textAlign: 'right',
  },

  infoRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  billedToBox: {
    width: '48%',
  },
  invoiceDetailsBox: {
    width: '48%',
  },
  sectionLabel: {
    fontSize: 9,
    color: colors.textLight,
    marginBottom: 6,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  billedToName: {
    fontSize: 12,
    fontWeight: 'bold',
    color: colors.textDark,
    marginBottom: 4,
  },
  billedToAddress: {
    fontSize: 9,
    color: colors.textMedium,
    marginBottom: 2,
    fontWeight: 'normal',
  },
  invoiceDetailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 6,
    paddingBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  invoiceDetailLabel: {
    fontSize: 9,
    color: colors.textLight,
  },
  invoiceDetailValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.textDark,
  },

  tableSectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.textDark,
    marginBottom: 8,
    letterSpacing: 0.5,
  },
  table: {
    marginBottom: 20,
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: colors.backgroundGray,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  tableHeaderText: {
    fontSize: 8,
    fontWeight: 'bold',
    color: colors.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  tableRow: {
    flexDirection: 'row',
    paddingVertical: 9,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  tableRowAlt: {
    flexDirection: 'row',
    paddingVertical: 9,
    paddingHorizontal: 10,
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
    backgroundColor: '#fcfcfc',
  },
  col1: { width: '40%' },
  col2: { width: '15%', textAlign: 'center' },
  col3: { width: '22%', textAlign: 'right' },
  col4: { width: '23%', textAlign: 'right' },
  cellText: {
    fontSize: 9,
    color: colors.textDark,
    fontWeight: 'bold',
  },
  cellTextLight: {
    fontSize: 9,
    color: colors.textMedium,
    fontWeight: 'bold',
  },

  totalsContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 8,
    alignItems: 'flex-start',
  },
  totalsBox: {
    width: '45%',
  },
  signatureSection: {
    width: '45%',
    paddingTop: 10,
  },
  signatureRow: {
    marginBottom: 20,
  },
  signatureLabel: {
    fontSize: 7,
    color: colors.textLight,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
    marginBottom: 10,
  },
  signatureValue: {
    fontSize: 7,
    color: colors.textDark,
    fontWeight: 'bold',
    borderBottomWidth: 1,
    borderBottomColor: colors.textLight,
    paddingBottom: 2,
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
    paddingHorizontal: 8,
  },
  totalsLabel: {
    fontSize: 9,
    color: colors.textMedium,
    fontWeight: 'bold',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  totalsValue: {
    fontSize: 9,
    color: colors.textDark,
  },
  discountRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
    paddingHorizontal: 8,
  },
  discountLabel: {
    fontSize: 9,
    color: colors.red,
    fontWeight: 'normal',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  discountValue: {
    fontSize: 9,
    color: colors.red,
    fontWeight: 'normal',
  },
  surchargeRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 5,
    paddingHorizontal: 8,
  },
  surchargeLabel: {
    fontSize: 9,
    color: colors.blue,
    fontWeight: 'normal',
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  surchargeValue: {
    fontSize: 9,
    color: colors.blue,
    fontWeight: 'normal',
  },
  grandTotalRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 8,
    paddingHorizontal: 8,
    marginTop: 4,
    borderTopWidth: 1,
    borderTopColor: colors.border,
    backgroundColor: colors.backgroundGray,
  },
  grandTotalLabel: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.textDark,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  grandTotalValue: {
    fontSize: 10,
    fontWeight: 'bold',
    color: colors.textDark,
  },

  paidBalanceBox: {
    marginTop: 6,
    borderWidth: 1,
    borderColor: colors.border,
    borderRadius: 4,
  },
  paidRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  paidLabel: {
    fontSize: 9,
    color: colors.textMedium,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  paidValue: {
    fontSize: 9,
    color: colors.textMedium,
    fontWeight: 'bold',
  },
  paidDivider: {
    borderBottomWidth: 1,
    borderBottomColor: colors.borderLight,
  },
  balanceRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  balanceLabel: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.textDark,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
  },
  balanceValue: {
    fontSize: 9,
    fontWeight: 'bold',
    color: colors.primary,
  },

  footer: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#F1F7FF',
    paddingVertical: 12,
    paddingHorizontal: 40,
  },
  footerText: {
    fontSize: 10,
    color: colors.primary,
    fontWeight: 'bold',
    marginRight: 8,
    letterSpacing: 1,
  },
  footerLogo: {
    width: 60,
    height: 20,
    marginBottom: 2,
  },
});

const formatAddress = (address: {
  addressLine1: string;
  addressLine2?: string;
  city: string;
  postCode: string;
  country: string;
}): string[] => {
  const lines = [address.addressLine1];
  if (address.addressLine2) {
    lines.push(address.addressLine2);
  }
  lines.push(`${address.city}, ${address.postCode}`);
  lines.push(address.country);
  return lines;
};

interface InvoicePDFProps {
  order: OrderViewData;
  companyInfo?: CompanyInfoData;
}

export const InvoiceDocument: React.FC<InvoicePDFProps> = ({
  order,
  companyInfo,
}) => {
  const subtotal = order.items.reduce((sum, item) => sum + item.subTotal, 0);

  const discountAmount = (() => {
    if (!order.discount) return 0;
    if (order.discount.amount !== null && order.discount.amount !== undefined) {
      return parseFloat(order.discount.amount.toString());
    }
    if (
      order.discount.percentage !== null &&
      order.discount.percentage !== undefined
    ) {
      return (
        (subtotal * parseFloat(order.discount.percentage.toString())) / 100
      );
    }
    return 0;
  })();

  const netTotal = subtotal - discountAmount;

  const resolvedSurcharges: Array<{ name: string; amount: number }> = (() => {
    if (!order.surcharges) return [];
    if (typeof order.surcharges === 'number') {
      return [{ name: 'Surcharge', amount: order.surcharges }];
    }
    if (Array.isArray(order.surcharges)) {
      return (
        order.surcharges as Array<{
          name: string;
          amount: number | null;
          percentage: number | null;
        }>
      ).map((surcharge) => {
        let resolvedAmount = 0;
        if (surcharge.amount !== null && surcharge.amount !== undefined) {
          resolvedAmount = parseFloat(surcharge.amount.toString());
        } else if (
          surcharge.percentage !== null &&
          surcharge.percentage !== undefined
        ) {
          resolvedAmount =
            (subtotal * parseFloat(surcharge.percentage.toString())) / 100;
        }
        return { name: surcharge.name || 'Surcharge', amount: resolvedAmount };
      });
    }
    return [];
  })();

  const totalPaidAmount =
    (order as { totalPaidAmount?: number }).totalPaidAmount ?? 0;
  const balanceDue = order.grandTotal - totalPaidAmount;

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.footer} fixed>
          <Text style={styles.footerText}>POWERED BY</Text>
          <Image style={styles.footerLogo} src={Logo} />
        </View>

        {/* ===== HEADER ROW ===== */}
        <View style={styles.headerRow}>
          <View style={styles.headerLeft}>
            {companyInfo?.logoUrl && (
              <Image style={styles.companyLogo} src={companyInfo.logoUrl} />
            )}
            <View style={styles.companyTextBlock}>
              <Text style={styles.companyName}>
                {companyInfo?.companyName || 'OrderX'}
              </Text>
              {companyInfo && (
                <>
                  <Text style={styles.companyDetails}>
                    {companyInfo.addressLine1}
                    {companyInfo.addressLine2
                      ? `, ${companyInfo.addressLine2}`
                      : ''}
                  </Text>
                  <Text style={styles.companyDetails}>
                    {companyInfo.city}, {companyInfo.postCode}
                  </Text>
                  <Text style={styles.companyCountry}>
                    {companyInfo.country}
                  </Text>
                  {companyInfo.telephoneNumber && (
                    <Text style={styles.companyDetails}>
                      Tel: {companyInfo.telephoneNumber}
                    </Text>
                  )}
                  {companyInfo.email && (
                    <Text style={styles.companyDetails}>
                      Email: {companyInfo.email}
                    </Text>
                  )}
                  {companyInfo.websiteUrl && (
                    <Text style={styles.companyDetails}>
                      Web: {companyInfo.websiteUrl}
                    </Text>
                  )}
                </>
              )}
            </View>
          </View>
        </View>

        {/* ===== BILLED TO + INVOICE DETAILS ===== */}
        <View style={styles.infoRow}>
          {/* Billed To */}
          <View style={styles.billedToBox}>
            <Text style={styles.sectionLabel}>Billed to</Text>
            <Text style={styles.billedToName}>{order.customerName}</Text>
            {formatAddress(order.billingAddress).map((line, idx) => (
              <Text key={idx} style={styles.billedToAddress}>
                {line}
              </Text>
            ))}
            {order.billingAddress.contactNo && (
              <Text style={styles.billedToAddress}>
                Tel: {order.billingAddress.contactNo}
              </Text>
            )}
          </View>

          {/* Invoice Details */}
          <View style={styles.invoiceDetailsBox}>
            <View style={styles.invoiceDetailRow}>
              <Text style={styles.invoiceDetailLabel}>Invoice Number</Text>
              <Text style={styles.invoiceDetailValue}>
                #{order.orderNumber}
              </Text>
            </View>
            <View style={styles.invoiceDetailRow}>
              <Text style={styles.invoiceDetailLabel}>Order Date</Text>
              <Text style={styles.invoiceDetailValue}>
                {formatDate(order.createdAt)}
              </Text>
            </View>
            <View style={styles.invoiceDetailRow}>
              <Text style={styles.invoiceDetailLabel}>Payment Status</Text>
              <Text style={styles.invoiceDetailValue}>{order.orderStatus}</Text>
            </View>
            <View style={styles.invoiceDetailRow}>
              <Text style={styles.invoiceDetailLabel}>Delivery Date</Text>
              <Text style={styles.invoiceDetailValue}>
                {formatDate(
                  order.actualDeliveryDate || order.scheduledDeliveryDate
                )}
              </Text>
            </View>
          </View>
          <View style={styles.headerRight}>
            <Text style={styles.grandTotalHeaderLabel}>Grand Total</Text>
            <Text style={styles.grandTotalHeaderValue}>
              {formatCurrency(order.grandTotal)}
            </Text>
          </View>
        </View>

        <Text style={styles.tableSectionTitle}>Product Details</Text>
        <View style={styles.table}>
          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.tableHeaderText, styles.col1]}>Product</Text>
            <Text style={[styles.tableHeaderText, styles.col2]}>Qty</Text>
            <Text style={[styles.tableHeaderText, styles.col3]}>
              Unit Price
            </Text>
            <Text style={[styles.tableHeaderText, styles.col4]}>Amount</Text>
          </View>
          {/* Table Rows */}
          {order.items.map((item, index) => (
            <View
              key={index}
              style={index % 2 === 0 ? styles.tableRow : styles.tableRowAlt}
            >
              <Text style={[styles.cellText, styles.col1]}>{item.name}</Text>
              <Text style={[styles.cellTextLight, styles.col2]}>
                {item.quantity.toLocaleString('en-US')}
              </Text>
              <Text style={[styles.cellTextLight, styles.col3]}>
                {formatCurrency(item.unitPrice)}
              </Text>
              <Text style={[styles.cellText, styles.col4]}>
                {formatCurrency(item.subTotal)}
              </Text>
            </View>
          ))}
        </View>

        <View style={styles.totalsContainer}>
          <View style={styles.signatureSection}>
            <View style={styles.signatureRow}>
              <Text style={styles.signatureLabel}>Prepared By</Text>
              <Text style={styles.signatureValue}>
                {order.preparedBy || ' '}
              </Text>
            </View>
            <View style={styles.signatureRow}>
              <Text style={styles.signatureLabel}>Approved By</Text>
              <Text style={styles.signatureValue}>
                {order.approvedBy || ' '}
              </Text>
            </View>
          </View>
          <View style={styles.totalsBox}>
            {/* Subtotal */}
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>Items Subtotal</Text>
              <Text style={styles.totalsValue}>{formatCurrency(subtotal)}</Text>
            </View>

            {/* Discount */}
            <View style={styles.discountRow}>
              <Text style={styles.discountLabel}>Discount</Text>
              <Text style={styles.discountValue}>
                {discountAmount > 0
                  ? `- ${formatCurrency(discountAmount)}`
                  : formatCurrency(0)}
              </Text>
            </View>

            {/* Net Total */}
            <View style={styles.totalsRow}>
              <Text style={styles.totalsLabel}>Net Total</Text>
              <Text style={styles.totalsValue}>{formatCurrency(netTotal)}</Text>
            </View>

            {resolvedSurcharges.length > 0 ? (
              resolvedSurcharges.map((surcharge, idx) => (
                <View style={styles.surchargeRow} key={idx}>
                  <Text style={styles.surchargeLabel}>{surcharge.name}</Text>
                  <Text style={styles.surchargeValue}>
                    {surcharge.amount > 0
                      ? `+ ${formatCurrency(surcharge.amount)}`
                      : formatCurrency(0)}
                  </Text>
                </View>
              ))
            ) : (
              <View style={styles.surchargeRow}>
                <Text style={styles.surchargeLabel}>Surcharges</Text>
                <Text style={styles.surchargeValue}>{formatCurrency(0)}</Text>
              </View>
            )}

            <View style={styles.grandTotalRow}>
              <Text style={styles.grandTotalLabel}>Grand Total</Text>
              <Text style={styles.grandTotalValue}>
                {formatCurrency(order.grandTotal)}
              </Text>
            </View>

            <View style={styles.paidBalanceBox}>
              <View style={styles.paidRow}>
                <Text style={styles.paidLabel}>Total Paid Amount</Text>
                <Text style={styles.paidValue}>
                  {formatCurrency(totalPaidAmount)}
                </Text>
              </View>
              <View style={styles.paidDivider} />
              <View style={styles.balanceRow}>
                <Text style={styles.balanceLabel}>Paid to be Amount</Text>
                <Text style={styles.balanceValue}>
                  {formatCurrency(balanceDue)}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};

export const generateInvoicePDF = async (
  order: OrderViewData
): Promise<void> => {
  try {
    const companyInfo = await getCompanyInfoForPDF();
    const blob = await pdf(
      <InvoiceDocument order={order} companyInfo={companyInfo} />
    ).toBlob();
    const fileName = `Invoice-${order.orderNumber}.pdf`;
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    link.target = '_blank';
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 100);
  } catch (error) {
    console.error('Error generating invoice PDF:', error);
    throw error;
  }
};

export default InvoiceDocument;
